/**
 * Pure data adapter: raw Adherence API response + Flow Planner intervals
 * → per-process, per-interval ACTUALS { volume, rate, headcount }. UI-free and
 * side-effect-free so it can be unit tested in isolation.
 *
 * Response shape: timeWindowAdherences[] = { type, activeShifts[],
 * timeWindow{startInSeconds, endInSeconds}, processAdherences[{ entityAttrs{process},
 * actual, planned }] }.
 *
 * Verified against a real response:
 *  - Epoch matches parseLocalISO (local wall-clock as UTC seconds), so window times
 *    align directly with interval startTime/endTime — no timezone conversion here.
 *  - volume/rate live under activeShifts[0] === <cycle>; headcount only under
 *    'ALL_CYCLES' (per-process station-wide, exact only for single-cycle plans).
 *  - A process's windows can overlap (mixed 1/5/10/15-min widths). Resolved
 *    widest-wins, then bucketed by midpoint containment so a coarse window
 *    straddling a boundary is neither double counted nor dropped.
 *    volume = SUM; rate/headcount = plain AVERAGE.
 */

export const ALL_CYCLES_SHIFT = 'ALL_CYCLES';

// True when any `actual` is non-null. Use this, not timeWindowAdherences.length —
// a plan not yet started returns many windows whose actuals are all null.
export function responseHasActuals(response) {
  const tw = response?.timeWindowAdherences;
  if (!Array.isArray(tw)) return false;
  return tw.some((w) => (w?.processAdherences || []).some((pa) => pa?.actual !== null && pa?.actual !== undefined));
}

const ADHERENCE_TYPE = {
  VOLUME: 'volume',
  RATE: 'rate',
  HEADCOUNT: 'headcount'
};

const shiftOf = (tw) => (Array.isArray(tw?.activeShifts) && tw.activeShifts.length > 0 ? tw.activeShifts[0] : null);

// { start, end, value } points for one adherence `type` scoped to `shift`, grouped
// by raw process name. Null actuals are skipped.
function collectSeriesByProcess(timeWindowAdherences, type, shift) {
  const byProcess = {};
  (timeWindowAdherences || []).forEach((tw) => {
    if (tw?.type !== type || shiftOf(tw) !== shift) return;
    const start = tw?.timeWindow?.startInSeconds;
    const end = tw?.timeWindow?.endInSeconds;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return;
    (tw.processAdherences || []).forEach((pa) => {
      const process = pa?.entityAttrs?.process;
      const actual = pa?.actual;
      if (!process || actual === null || actual === undefined) return;
      if (!byProcess[process]) byProcess[process] = [];
      byProcess[process].push({ start, end, value: actual });
    });
  });
  return byProcess;
}

// Where two windows overlap, keep the wider one. Deterministic (sorted by start,
// then descending width); returns a non-overlapping list.
export function resolveNonOverlapping(windows) {
  const sorted = [...(windows || [])].sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start));
  const kept = [];
  sorted.forEach((w) => {
    const overlapIdx = [];
    for (let i = 0; i < kept.length; i++) {
      if (w.start < kept[i].end && kept[i].start < w.end) overlapIdx.push(i);
    }
    if (overlapIdx.length === 0) {
      kept.push(w);
      return;
    }
    const wWidth = w.end - w.start;
    const loses = overlapIdx.some((i) => kept[i].end - kept[i].start >= wWidth);
    if (loses) return; // an existing overlapping window is wider/equal → drop new
    // new window is strictly wider than all overlaps → replace them
    overlapIdx.sort((a, b) => b - a).forEach((i) => kept.splice(i, 1));
    kept.push(w);
  });
  return kept;
}

// Assign each window to the interval containing its midpoint, then aggregate
// ('sum' for volume, 'avg' for rate/hc). Intervals with no data return null.
function bucketByInterval(windows, intervals, aggregate) {
  const buckets = intervals.map(() => []);
  (windows || []).forEach((w) => {
    const mid = (w.start + w.end) / 2;
    for (let i = 0; i < intervals.length; i++) {
      const iv = intervals[i];
      if (mid >= iv.startTime && mid < iv.endTime) {
        buckets[i].push(w.value);
        break;
      }
    }
  });
  return buckets.map((vals) => {
    if (vals.length === 0) return null;
    const total = vals.reduce((s, v) => s + v, 0);
    return aggregate === 'sum' ? total : total / vals.length;
  });
}

function bucketSeries(seriesByProcess, intervals, aggregate) {
  const out = {};
  Object.entries(seriesByProcess).forEach(([process, windows]) => {
    out[process] = bucketByInterval(resolveNonOverlapping(windows), intervals, aggregate);
  });
  return out;
}

// True when a bucketed map holds at least one real value; a process can appear in
// the map with every interval null.
const hasAnyValue = (map) => Object.values(map).some((arr) => (arr || []).some((v) => v !== null));

/**
 * Per-process, per-interval actuals for one cycle. Returns
 * { byProcess: { [process]: { volume[], rate[], headcount[] } }, hasData, isMultiCycle };
 * each metric array is indexed by interval, null meaning "no actual".
 *
 * hasData covers volume/rate only. Headcount is station-wide, so counting it would
 * report data for a cycle that has none of its own actuals yet.
 */
export function buildAdherenceActuals(response, intervals, cycleName) {
  const empty = { byProcess: {}, hasData: false, isMultiCycle: false };
  const tw = response?.timeWindowAdherences;
  if (!Array.isArray(tw) || tw.length === 0 || !Array.isArray(intervals) || intervals.length === 0 || !cycleName) {
    return empty;
  }

  const volume = bucketSeries(collectSeriesByProcess(tw, ADHERENCE_TYPE.VOLUME, cycleName), intervals, 'sum');
  const rate = bucketSeries(collectSeriesByProcess(tw, ADHERENCE_TYPE.RATE, cycleName), intervals, 'avg');
  // Station-wide (ALL_CYCLES) only. Kept although the cycle-scoped grid no longer
  // shows it — reused when building station-level Planned-vs-Actual metrics.
  const headcount = bucketSeries(
    collectSeriesByProcess(tw, ADHERENCE_TYPE.HEADCOUNT, ALL_CYCLES_SHIFT),
    intervals,
    'avg'
  );

  const byProcess = {};
  const merge = (metric, map) => {
    Object.entries(map).forEach(([process, arr]) => {
      if (!byProcess[process]) byProcess[process] = { volume: null, rate: null, headcount: null };
      byProcess[process][metric] = arr;
    });
  };
  merge('volume', volume);
  merge('rate', rate);
  merge('headcount', headcount);

  // Multi-cycle when more than one non-ALL_CYCLES shift reports volume.
  const cycleShifts = new Set();
  tw.forEach((w) => {
    const s = shiftOf(w);
    if (w?.type === ADHERENCE_TYPE.VOLUME && s && s !== ALL_CYCLES_SHIFT) cycleShifts.add(s);
  });

  return {
    byProcess,
    hasData: hasAnyValue(volume) || hasAnyValue(rate),
    isMultiCycle: cycleShifts.size > 1
  };
}

// First matching candidate process name for a grid row, or null.
export function matchProcessActuals(byProcess, candidates) {
  if (!byProcess) return null;
  for (const name of candidates || []) {
    if (name && byProcess[name]) return byProcess[name];
  }
  return null;
}
